import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, todayIso } from '../lib/format.ts';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

type Basis = 'issued' | 'paid';
type Method = 'payroll' | 'bank' | 'cash';
interface Payout {
  id: string;
  amount: number;
  base: number;
  method: Method;
  paid_on: string;
  note: string | null;
}
interface Row {
  user: string;
  name: string;
  rate: number;
  basis: Basis;
  active: boolean;
  invoices: number;
  base: number;
  amount: number;
  payout: Payout | null;
}
interface Overview {
  period: string;
  closed: boolean;
  rows: Row[];
  total: number;
  can_manage: boolean;
}
interface Statement {
  name: string;
  period: string;
  plan: { rate: number; basis: Basis } | null;
  lines: { invoice: string; name: string; date: string; customer: string; base: number; amount: number; state: string }[];
  base: number;
  amount: number;
  payout: Payout | null;
}
interface Person {
  id: string;
  name: string;
  plan: { rate: number; basis: Basis; active: boolean } | null;
}

const METHODS: { key: Method; icon: 'users' | 'bank' | 'cash' }[] = [
  { key: 'payroll', icon: 'users' },
  { key: 'bank', icon: 'bank' },
  { key: 'cash', icon: 'cash' },
];
const pct = (bp: number) => `${(bp / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} %`;
const shiftMonth = (p: string, n: number) => {
  const [y, m] = p.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
};
const monthName = (p: string) => new Date(`${p}-15T12:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });

/**
 * What salespeople earned on the invoices they wrote. A salesperson sees their own; managers
 * and accountants see everyone, set the plans and settle a month once it is over.
 */
export function CommissionsTab({ onOpenInvoice }: { onOpenInvoice: (id: string) => void }) {
  const [period, setPeriod] = useState(todayIso().slice(0, 7));
  const [v, setV] = useState(0);
  const { data } = useAction<Overview>('invoicing.commissions', { period, v });
  const [open, setOpen] = useState<string | null>(null);
  const [plans, setPlans] = useState(false);
  const d = data && data.period === period ? data : null;
  const settled = d ? d.rows.filter((r) => r.payout).reduce((s, r) => s + r.amount, 0) : 0;
  const thisMonth = todayIso().slice(0, 7);

  return (
    <div className="com-tab">
      <div className="toolbar com-toolbar">
        <div className="com-month" role="group" aria-label={t('com.month')}>
          <button className="icon-btn btn-ghost" aria-label={t('com.prev_month')} onClick={() => setPeriod(shiftMonth(period, -1))}>
            <Icon name="back" size={18} />
          </button>
          <strong className="com-month-name">{monthName(period)}</strong>
          <button className="icon-btn btn-ghost" aria-label={t('com.next_month')} onClick={() => setPeriod(shiftMonth(period, 1))} disabled={period >= thisMonth}>
            <Icon name="chevronRight" size={18} />
          </button>
        </div>
        {d?.can_manage ? (
          <Button icon="settings" onClick={() => setPlans(true)}>
            {t('com.plans')}
          </Button>
        ) : null}
      </div>
      {!d ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : !d.rows.length ? (
        <section className="panel">
          <EmptyState
            icon="percent"
            title={d.can_manage ? t('com.none_title') : t('com.none_mine')}
            body={d.can_manage ? t('com.none_body') : t('com.none_mine_body')}
            action={
              d.can_manage ? (
                <Button variant="primary" icon="plus" onClick={() => setPlans(true)}>
                  {t('com.set_plans')}
                </Button>
              ) : undefined
            }
          />
        </section>
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('com.title')}>
            <Kpi label={t('com.k_earned')} value={money(d.total)} hint={d.closed ? t('com.k_month_over') : t('com.k_so_far')} />
            <Kpi label={t('com.k_settled')} value={money(settled)} hint={tn('com.k_people', d.rows.filter((r) => r.payout).length)} />
            <Kpi label={t('com.k_to_settle')} value={money(d.total - settled)} hint={d.closed ? t('com.k_can_settle') : t('com.k_after_month')} warn={d.closed && d.total - settled > 0} />
          </section>
          <section className="panel" aria-labelledby="com-people">
            <div className="panel-head">
              <h2 id="com-people">{t('com.people')}</h2>
            </div>
            <Table
              head={[t('com.c_person'), t('com.c_plan'), t('com.c_invoices'), t('com.c_base'), t('com.c_earned'), t('com.c_state')]}
              phoneHide={[1, 2, 3]}
              rows={d.rows.map((r) => ({
                key: r.user,
                onClick: () => setOpen(r.user),
                cells: [
                  <span className="strong">{r.name}</span>,
                  <span className="muted">{t(`com.plan_${r.basis}`, { rate: pct(r.rate) })}</span>,
                  <span className="tabular">{r.invoices}</span>,
                  <span className="tabular">{money(r.base)}</span>,
                  <span className="tabular strong">{money(r.amount)}</span>,
                  r.payout ? (
                    <StatusBadge tone="ok" icon="check" label={t('com.settled_on', { day: day(r.payout.paid_on) })} />
                  ) : d.closed && r.amount > 0 ? (
                    <StatusBadge tone="warn" icon="clock" label={t('com.to_settle')} />
                  ) : (
                    <StatusBadge tone="neutral" icon="clock" label={d.closed ? t('com.nothing') : t('com.running')} />
                  ),
                ],
              }))}
            />
          </section>
        </>
      )}
      {open ? (
        <StatementDialog
          user={open}
          period={period}
          closed={!!d?.closed}
          manage={!!d?.can_manage}
          onClose={() => setOpen(null)}
          onOpenInvoice={(id) => (setOpen(null), onOpenInvoice(id))}
          onSettled={() => setV((x) => x + 1)}
        />
      ) : null}
      {plans ? <PlansDialog onClose={() => setPlans(false)} onSaved={() => setV((x) => x + 1)} /> : null}
    </div>
  );
}

function StatementDialog({
  user,
  period,
  closed,
  manage,
  onClose,
  onOpenInvoice,
  onSettled,
}: {
  user: string;
  period: string;
  closed: boolean;
  manage: boolean;
  onClose: () => void;
  onOpenInvoice: (id: string) => void;
  onSettled: () => void;
}) {
  const [v, setV] = useState(0);
  const { data: s } = useAction<Statement>('invoicing.commission_statement', { user, period, v });
  const { busy, run } = useSave();
  const [method, setMethod] = useState<Method>('payroll');
  const [paidOn, setPaidOn] = useState(todayIso());
  const [note, setNote] = useState('');
  const canSettle = manage && closed && s && !s.payout && s.amount > 0;
  const settle = () =>
    void run(async () => {
      await action('invoicing.settle_commission', { user, period, method, paid_on: paidOn, note: note.trim() || null });
      setV((x) => x + 1);
      onSettled();
    }, t('com.settled_toast', { name: s?.name ?? '', amount: money(s?.amount ?? 0) }));

  return (
    <Dialog title={s ? t('com.statement_title', { name: s.name, month: monthName(period) }) : t('com.title')} onClose={onClose} size="lg">
      {!s ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('com.c_plan')}</span>
              <strong>{s.plan ? t(`com.plan_${s.plan.basis}`, { rate: pct(s.plan.rate) }) : '—'}</strong>
            </div>
            <div>
              <span className="muted">{s.plan?.basis === 'paid' ? t('com.base_paid') : t('com.base_issued')}</span>
              <strong>{money(s.base)}</strong>
            </div>
            <div>
              <span className="muted">{t('com.k_earned')}</span>
              <strong>{money(s.amount)}</strong>
            </div>
            {s.payout ? <StatusBadge tone="ok" icon="check" label={t('com.settled_on', { day: day(s.payout.paid_on) })} /> : null}
          </div>
          {s.payout ? (
            <p className="callout">
              <Icon name="checkCircle" size={16} /> {t(`com.paid_${s.payout.method}`, { day: day(s.payout.paid_on), amount: money(s.payout.amount) })}
              {s.payout.note ? ` · ${s.payout.note}` : ''}
            </p>
          ) : null}
          <Table
            head={[t('com.c_invoice'), t('com.c_date'), t('com.c_customer'), s.plan?.basis === 'paid' ? t('com.c_paid_base') : t('com.c_net'), t('com.c_earned')]}
            phoneHide={[1, 2]}
            left={[2]}
            empty={t('com.no_lines')}
            rows={s.lines.map((l, i) => ({
              key: `${l.invoice}:${i}`,
              onClick: () => onOpenInvoice(l.invoice),
              cells: [
                <span className="tabular">{l.name}</span>,
                <span className="tabular">{day(l.date)}</span>,
                l.customer,
                <span className="tabular">{money(l.base)}</span>,
                <span className={`tabular strong${l.amount < 0 ? ' text-danger' : ''}`}>{money(l.amount)}</span>,
              ],
            }))}
            foot={s.lines.length ? [t('com.total'), '', '', money(s.base), money(s.amount)] : null}
          />
          {manage && !s.payout && !closed ? <p className="muted small">{t('com.settle_later')}</p> : null}
          {canSettle ? (
            <section className="pay-box" aria-labelledby="com-settle">
              <h3 id="com-settle">{t('com.settle_title', { amount: money(s.amount) })}</h3>
              <div className="segmented" role="radiogroup" aria-label={t('com.how_paid')}>
                {METHODS.map((m) => (
                  <button key={m.key} role="radio" aria-checked={method === m.key} className={method === m.key ? 'active' : ''} onClick={() => setMethod(m.key)}>
                    <Icon name={m.icon} size={16} /> {t(`com.m_${m.key}`)}
                  </button>
                ))}
              </div>
              <p className="muted small">{t(`com.m_${method}_hint`)}</p>
              <div className="form-grid">
                <Field label={t('com.paid_on')}>
                  <input className="input" type="date" value={paidOn} max={todayIso()} onChange={(e) => setPaidOn(e.target.value)} />
                </Field>
                <Field label={t('com.note')} hint={t('com.note_hint')}>
                  <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
                </Field>
              </div>
              <div className="form-actions">
                <Button variant="primary" icon="check" onClick={settle} busy={busy}>
                  {t('com.settle', { amount: money(s.amount) })}
                </Button>
              </div>
            </section>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function PlansDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { data: people, reload } = useAction<Person[]>('invoicing.commission_plans');
  return (
    <Dialog title={t('com.plans')} onClose={onClose} size="lg">
      <div className="form-stack">
        <p className="muted">{t('com.plans_intro')}</p>
        {!people ? (
          <Skeleton h={200} />
        ) : (
          <div className="list-card com-plans">
            {people.map((p) => (
              <PlanRow key={p.id} person={p} onSaved={async () => (await reload(), onSaved())} />
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function PlanRow({ person: p, onSaved }: { person: Person; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [rate, setRate] = useState(p.plan ? String(p.plan.rate / 100) : '');
  const [basis, setBasis] = useState<Basis>(p.plan?.basis ?? 'paid');
  const [active, setActive] = useState(p.plan?.active ?? true);
  const rateN = rate.trim() === '' ? null : Number(rate.trim().replace(',', '.'));
  const bad = rate.trim() !== '' && (rateN === null || !Number.isFinite(rateN) || rateN < 0 || rateN > 50);
  const dirty = p.plan ? rateN !== p.plan.rate / 100 || basis !== p.plan.basis || active !== p.plan.active : rateN !== null;
  const save = () =>
    void run(async () => {
      await action('invoicing.save_commission_plan', { user: p.id, rate: rateN ?? 0, basis, active });
      await onSaved();
    }, t('com.plan_saved', { name: p.name }));
  return (
    <div className="list-row com-plan-row">
      <span className="list-main">
        <span className="list-title">{p.name}</span>
        <span className="list-sub">{p.plan ? (p.plan.active ? t(`com.plan_${p.plan.basis}`, { rate: pct(p.plan.rate) }) : t('com.plan_off')) : t('com.no_plan')}</span>
      </span>
      <span className="input-unit com-rate">
        <input className={`input${bad ? ' invalid' : ''}`} inputMode="decimal" value={rate} placeholder="0" aria-label={t('com.rate_for', { name: p.name })} onChange={(e) => setRate(e.target.value)} />
        <span>%</span>
      </span>
      <select className="input com-basis" value={basis} aria-label={t('com.basis_for', { name: p.name })} onChange={(e) => setBasis(e.target.value as Basis)}>
        <option value="paid">{t('com.basis_paid')}</option>
        <option value="issued">{t('com.basis_issued')}</option>
      </select>
      {p.plan ? <Switch checked={active} label={t('com.active_for', { name: p.name })} onChange={setActive} /> : <span className="com-switch-gap" />}
      <Button size="sm" variant={dirty ? 'primary' : 'secondary'} icon="check" onClick={save} busy={busy} disabled={!dirty || bad}>
        {t('common.save')}
      </Button>
    </div>
  );
}
